import { Aperture, CircleHelp, Compass, Home, Library, LogOut, Menu, PanelRight, PenLine, Search, Sprout, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { ChangePasswordButton } from "./ChangePasswordButton";
import { LanguageToggle } from "./LanguageToggle";

export type AppTab = "capture" | "library" | "grow";

type AppShellProps = {
  left: ReactNode;
  main: ReactNode;
  right?: ReactNode;
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  onLogout: () => void;
  onSearch: (query: string) => void;
  onOpenGuide?: () => void;
};

const navItems = [
  { value: "capture", label: "Ghi chép", icon: PenLine, enabled: true },
  { value: "library", label: "Thư viện", icon: Library, enabled: true },
  { value: "grow", label: "Phát triển", icon: TrendingUp, enabled: true }
] satisfies Array<{ value: AppTab; label: string; icon: typeof Home; enabled: boolean }>;

export function AppShell({ left, main, right, activeTab, onTabChange, onLogout, onSearch, onOpenGuide }: AppShellProps) {
  const isPreview = import.meta.env.VITE_APP_ENV === "preview";
  const [query, setQuery] = useState("");
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);

  const drawerOpen = leftOpen || rightOpen;

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLeftOpen(false);
        setRightOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [drawerOpen]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1280px)");
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setLeftOpen(false);
        setRightOpen(false);
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const handleTabChange = (tab: AppTab) => {
    setLeftOpen(false);
    setRightOpen(false);
    onTabChange(tab);
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-20 border-b border-line bg-white/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-[1560px] flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          {left ? (
            <button
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-brand xl:hidden"
              type="button"
              aria-label="Mở danh sách nguồn"
              onClick={() => setLeftOpen(true)}
            >
              <Menu size={18} aria-hidden="true" />
            </button>
          ) : null}
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-brand">
              <Sprout size={21} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-base font-bold leading-tight">Human Note</p>
                {isPreview ? (
                  <span className="shrink-0 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
                    Preview
                  </span>
                ) : null}
              </div>
              <p className="hidden truncate text-xs font-medium text-muted sm:block">Ghi nhanh, sắp xếp, chắt lọc, liên kết</p>
            </div>
          </div>

          <div className="order-3 flex h-10 min-w-0 flex-1 items-center rounded-lg border border-line bg-slate-50 px-3 text-muted sm:order-none sm:min-w-[220px]">
            <Search size={17} aria-hidden="true" />
            <input
              className="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-sm outline-none placeholder:text-slate-400"
              placeholder="Tìm note, nguồn học, task, concept"
              aria-label="Search Human Note"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                onSearch(event.target.value);
              }}
            />
          </div>

          <nav className="flex items-center gap-1 overflow-x-auto">
            {right ? (
              <button
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-brand xl:hidden"
                type="button"
                aria-label="Mở bảng chi tiết"
                onClick={() => setRightOpen(true)}
              >
                <PanelRight size={18} aria-hidden="true" />
              </button>
            ) : null}
            {onOpenGuide ? (
              <button
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-transparent bg-white text-slate-600 hover:border-line hover:bg-slate-50"
                type="button"
                aria-label="Mở hướng dẫn"
                title="Hướng dẫn sử dụng"
                onClick={onOpenGuide}
              >
                <CircleHelp size={18} aria-hidden="true" />
              </button>
            ) : null}
            <LanguageToggle />
            <a
              href="/explore"
              aria-label="Khám phá"
              title="Khám phá nội dung công khai"
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-transparent bg-white px-3 text-sm font-semibold text-slate-600 hover:border-line hover:bg-slate-50 max-sm:w-10 max-sm:justify-center max-sm:px-0"
            >
              <Compass size={17} aria-hidden="true" />
              <span className="hidden sm:inline">Khám phá</span>
            </a>
            <a
              href="/moments"
              aria-label="Khoảnh khắc"
              title="Chụp & phân loại thói quen"
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-transparent bg-white px-3 text-sm font-semibold text-slate-600 hover:border-line hover:bg-slate-50 max-sm:w-10 max-sm:justify-center max-sm:px-0"
            >
              <Aperture size={17} aria-hidden="true" />
              <span className="hidden sm:inline">Khoảnh khắc</span>
            </a>
            {navItems.map((item) => (
              <button
                key={item.label}
                aria-current={activeTab === item.value ? "page" : undefined}
                aria-label={item.label}
                className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition max-sm:w-10 max-sm:justify-center max-sm:px-0 ${
                  activeTab === item.value
                    ? "border-blue-200 bg-blue-50 text-brand"
                    : !item.enabled
                      ? "cursor-not-allowed border-transparent bg-white text-slate-300"
                    : "border-transparent bg-white text-slate-600 hover:border-line hover:bg-slate-50"
                }`}
                type="button"
                disabled={!item.enabled}
                title={item.enabled ? item.label : `${item.label} coming soon`}
                onClick={() => {
                  if (item.enabled) {
                    handleTabChange(item.value);
                  }
                }}
              >
                <item.icon size={17} aria-hidden="true" />
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            ))}
            <ChangePasswordButton />
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-transparent bg-white text-slate-600 hover:border-line hover:bg-slate-50"
              type="button"
              aria-label="Sign out"
              onClick={onLogout}
            >
              <LogOut size={18} aria-hidden="true" />
            </button>
          </nav>
        </div>
      </header>

      <main
        className={`mx-auto grid max-w-[1560px] grid-cols-1 gap-4 px-4 py-4 sm:px-6 ${
          left && right
            ? "xl:grid-cols-[330px_minmax(0,1fr)_380px]"
            : left
              ? "xl:grid-cols-[330px_minmax(0,1fr)]"
              : right
                ? "xl:grid-cols-[minmax(0,1fr)_380px]"
                : ""
        }`}
      >
        {left ? (
          <aside
            className={`min-w-0 fixed inset-y-0 left-0 z-40 w-[88%] max-w-[360px] transform bg-paper shadow-panel transition-transform duration-300 ease-out overflow-y-auto overscroll-contain ${
              leftOpen ? "translate-x-0" : "-translate-x-full"
            } xl:inset-auto xl:left-auto xl:z-auto xl:w-auto xl:max-w-none xl:translate-x-0 xl:transform-none xl:bg-transparent xl:shadow-none xl:overflow-visible xl:sticky xl:top-20 xl:h-[calc(100vh-6rem)]`}
          >
            {left}
          </aside>
        ) : null}
        <section className="min-w-0">{main}</section>
        {right ? (
          <aside
            className={`min-w-0 fixed inset-y-0 right-0 z-40 w-[88%] max-w-[360px] transform bg-paper shadow-panel transition-transform duration-300 ease-out overflow-y-auto overscroll-contain ${
              rightOpen ? "translate-x-0" : "translate-x-full"
            } xl:inset-auto xl:right-auto xl:z-auto xl:w-auto xl:max-w-none xl:translate-x-0 xl:transform-none xl:bg-transparent xl:shadow-none xl:overflow-visible xl:sticky xl:top-20 xl:h-[calc(100vh-6rem)]`}
          >
            {right}
          </aside>
        ) : null}
      </main>

      {drawerOpen ? (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-[1px] xl:hidden"
          onClick={() => {
            setLeftOpen(false);
            setRightOpen(false);
          }}
          aria-hidden
        />
      ) : null}
    </div>
  );
}
